import type { ReactNode } from 'react';
import { createContext, use, useEffect, useState } from 'react';
import type { DropDownListOption, Programme } from '../types';
import { useCartContext } from './CartContext';
import { addToCart } from '../api';
import { hasProperty, isArrangementInCart } from '../utils';
import ErrorDialog from '../components/errorDialog/ErrorDialog';
import { useTranslation } from 'react-i18next';

type ProductPageContextType = {
    programme: Programme;
    countryCodes: DropDownListOption[];
    setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
};

const ProductPageContext = createContext<ProductPageContextType | undefined>(undefined);

export type ProductPageProviderProps = {
    programme: Programme;
    countryCodes: DropDownListOption[];
    children: ReactNode;
};

function addToCartErrorMessage(error: unknown) {
    return hasProperty(error, 'type') && error.type === 'ItemAlreadyAdded'
        ? 'cart:errorAlreadyExists'
        : 'cart:errorPostingToCart';
}

export const ProductPageProvider = ({ programme, countryCodes, children }: ProductPageProviderProps) => {
    const { t } = useTranslation();
    const { isAuthorized, cart, refreshCart, setCartOpen } = useCartContext();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Fetch arrangement from url and add to cart
    useEffect(() => {
        if (!isAuthorized) {
            return;
        }

        const findArrangementAndAddToCart = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const arrangementBannerId = urlParams.get('a');
            if (arrangementBannerId) {
                const foundArrangement = programme.applyItems
                    .flatMap((item) => (item.type === 'arrangement' ? [item] : []))
                    .find(
                        ({ bannerId, isOpenForApplicants }) => bannerId === arrangementBannerId && isOpenForApplicants,
                    );

                if (foundArrangement) {
                    // Remove arrangement parameter from URL
                    const url = new URL(window.location.href);
                    url.searchParams.delete('a');
                    history.replaceState(null, '', url);

                    if (isArrangementInCart(foundArrangement.bannerId, cart.items)) {
                        setCartOpen(true);
                    } else {
                        try {
                            await addToCart([foundArrangement]);
                            await refreshCart();
                            setCartOpen(true);
                        } catch (error) {
                            setErrorMessage(addToCartErrorMessage(error));
                        }
                    }
                }
            }
        };

        findArrangementAndAddToCart();
    }, [cart.items, isAuthorized, programme, refreshCart, setCartOpen, setErrorMessage]);

    return (
        <ProductPageContext value={{ programme, countryCodes, setErrorMessage }}>
            {children}
            {errorMessage && (
                <ErrorDialog
                    heading={t('cart:infoHeading')}
                    message={errorMessage}
                    onClose={() => setErrorMessage(null)}
                />
            )}
        </ProductPageContext>
    );
};

export const useProductPageContext = () => {
    const context = use(ProductPageContext);
    if (context === undefined) {
        throw new Error('useProductPageContext must be used within a ProductPageProvider');
    }
    return context;
};
